import React from 'react';
import { FormTemplateQuestionFieldOptionsValidityHintEnum } from 'graphql-common';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import Typography from '@lib/components/Typography/Typography';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import UserPreviewCell from '@lib/components/Table/commonComponents/UserPreviewCell/UserPreviewCell';
import useArrowKeyNavigation from '@lib/hooks/useArrowKeyNavigation';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import { dateTimeFormatEU } from '@lib/enums/dateTime';
import styles from './TaskCompletionDetails.module.scss';

type CompletionForHeader = {
  validityHint?: FormTemplateQuestionFieldOptionsValidityHintEnum | null;
  createdAt?: string | null;
  task?: {
    formTemplateVersion?: {
      version?: string | null;
    } | null;
  } | null;
  performer?: {
    firstName?: string | null;
    lastName?: string | null;
    avatarAttached?: {
      urls?: {
        small?: string | null;
      } | null;
    } | null;
  } | null;
};

type Props = {
  data: CompletionForHeader | null | undefined;
  loading: boolean;
  allIds: string[];
  onNavigatePrev?: () => void;
  onNavigateNext?: () => void;
  canNavigatePrev: boolean;
  canNavigateNext: boolean;
};

export default function TaskCompletionModalHeader(props: Props) {
  const {
    data,
    loading,
    allIds,
    onNavigatePrev,
    onNavigateNext,
    canNavigatePrev,
    canNavigateNext,
  } = props;

  const completionValidityHint = data?.validityHint;
  const createdAt = data?.createdAt
    ? getFormattedDate(data.createdAt, dateTimeFormatEU)
    : undefined;
  const version = data?.task?.formTemplateVersion?.version
    ? `v${data?.task?.formTemplateVersion?.version}`
    : undefined;
  const performerFirstName = data?.performer?.firstName || '';
  const performerLastName = data?.performer?.lastName || '';
  const performerName = [performerFirstName, performerLastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const performerAvatar = data?.performer?.avatarAttached?.urls?.small;

  useArrowKeyNavigation([
    {
      keys: 'ArrowLeft',
      handler: () => onNavigatePrev?.(),
      disabled: !onNavigatePrev || !canNavigatePrev,
    },
    {
      keys: 'ArrowRight',
      handler: () => onNavigateNext?.(),
      disabled: !onNavigateNext || !canNavigateNext,
    },
  ]);

  return (
    <div className={styles.header}>
      <div>
        {loading && <CircleLoader padding={0} />}
        {!loading && (
          <div className={styles.info}>
            {completionValidityHint ===
              FormTemplateQuestionFieldOptionsValidityHintEnum.Failure && (
              <>
                <div className={styles.infoItem}>
                  <Chip
                    type={ChipTypes.chipRed}
                    leftIcon="error"
                    size={ChipSizes.small}
                  />
                </div>
                <div className={styles.infoItemSeparator} />
              </>
            )}
            {completionValidityHint ===
              FormTemplateQuestionFieldOptionsValidityHintEnum.Success && (
              <>
                <div className={styles.infoItem}>
                  <Chip
                    type={ChipTypes.chipGreen}
                    leftIcon="check_circle"
                    size={ChipSizes.small}
                  />
                </div>
                <div className={styles.infoItemSeparator} />
              </>
            )}
            {completionValidityHint ===
              FormTemplateQuestionFieldOptionsValidityHintEnum.Warning && (
              <>
                <div className={styles.infoItem}>
                  <Chip
                    type={ChipTypes.chipOrange}
                    leftIcon="warning"
                    size={ChipSizes.small}
                  />
                </div>
                <div className={styles.infoItemSeparator} />
              </>
            )}
            <div className={styles.infoItem}>
              <Typography variant="body">{createdAt}</Typography>
            </div>
            <div className={styles.infoItemSeparator} />
            <div className={styles.infoItem}>
              <Chip label={version} type={ChipTypes.chipBlack} />
            </div>
            <div className={styles.infoItemSeparator} />
            <div className={styles.infoItem}>
              <UserPreviewCell name={performerName} avatar={performerAvatar} />
            </div>
          </div>
        )}
      </div>
      <div>
        {allIds && allIds.length > 1 ? (
          <div className={styles.nav}>
            <IconButton
              icon="arrow_back"
              type={IconButtonTypes.PrimaryOutlined}
              disabled={!canNavigatePrev}
              onClick={() => (onNavigatePrev ? onNavigatePrev() : undefined)}
            />
            <IconButton
              icon="arrow_forward"
              type={IconButtonTypes.PrimaryOutlined}
              disabled={!canNavigateNext}
              onClick={() => (onNavigateNext ? onNavigateNext() : undefined)}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
